// ============================================================================
// CHECKOUT CART SNIPPET - Function-Based Structure
// Manages cart display and syncs with Shopify cart
// ============================================================================

// ============================================================================
// CONSTANTS AND CONFIGURATION
// ============================================================================

const CART_CONSTANTS = {
  MOBILE_BREAKPOINT: 768,
  DRAG_THRESHOLD: 50,
  VELOCITY_THRESHOLD: 0.3,
  DEFAULT_SUBSCRIBE_DISCOUNT: 50,
  DEFAULT_TRY_ONCE_DISCOUNT: 40,
  DEFAULT_FREE_SHIPPING_THRESHOLD: 55,
  DEFAULT_MAX_PROGRESS_VALUE: 200,
  SYNC_DELAY: 100,
  INITIAL_SYNC_DELAY: 100,
  TOUCH_FEEDBACK_MAX: 10,
  QUANTITY_MIN: 1,
  QUANTITY_MAX: 999
};

const DEFAULT_SVGS = {
  toggleArrowUp: '<svg width="20" height="12" viewBox="0 0 20 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 11L10 2L19 11" stroke="#BDBDBD" stroke-width="1.63636"/></svg>',
  toggleArrowDown: '<svg width="20" height="12" viewBox="0 0 20 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L10 10L19 1" stroke="#BDBDBD" stroke-width="1.63636"/></svg>',
  defaultProductImage: '<img src="https://cdn.shopify.com/s/files/1/0012/5573/5405/files/image_15.png?v=1759168051" width="50" height="50"></img>',
  defaultProductName: '<svg width="179" height="16" viewBox="0 0 179 16" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="179" height="16" rx="8" fill="#E9E9E9"/></svg>',
  defaultQuantity: '<svg width="108" height="17" viewBox="0 0 108 17" fill="none" xmlns="http://www.w3.org/2000/svg"><rect y="0.493225" width="108" height="16" rx="8" fill="#E9E9E9"/></svg>'
};

const PRODUCT_NAME_PATTERNS = [
  ' - All-in-One Shake', ' - Shake tout-en-un', 'Green Superfood Juice - ',
  'Jus de superaliments verts - ', ' - Green Apple', ' - Pomme verte',
  '- Boisson tout-en-un', ' - All-in-One Superfood Shake',
  'Shake de superaliments tout-en-un - ', ' - 32g High Protein Shake',
  ' - Boisson hyperprotéinée 32g'
];

// ============================================================================
// GLOBAL STATE MANAGEMENT
// ============================================================================

function initializeGlobalState() {
if (!window.checkoutCartInstances) {
  window.checkoutCartInstances = [];
  }
  if (!window.checkoutCartToggleArrowUp) {
    window.checkoutCartToggleArrowUp = DEFAULT_SVGS.toggleArrowUp;
    window.checkoutCartToggleArrowDown = DEFAULT_SVGS.toggleArrowDown;
  }
}

function syncAllCheckoutCartInstances() {
  if (window.checkoutCartInstances?.length > 0) {
    window.checkoutCartInstances.forEach(instance => {
      setTimeout(() => instance.syncFunction(), CART_CONSTANTS.SYNC_DELAY);
    });
  }
}

// ============================================================================
// UPCART API INTEGRATION
// ============================================================================

function setupUpCartIntegration() {
window.addEventListener('DOMContentLoaded', function() {
  setTimeout(function() {
    const originalCartUpdated = window.upcartOnCartUpdated;
    const originalAddToCart = window.upcartOnAddToCart;
    const originalItemRemoved = window.upcartOnItemRemoved;
    const originalSelectUpsellItemSellingPlan = window.upcartSelectUpsellItemSellingPlan;
    
    const wrapCallback = (original, syncFn) => function(...args) {
      if (original && typeof original === 'function') original(...args);
      syncFn();
    };
    
      window.upcartOnCartUpdated = wrapCallback(originalCartUpdated, syncAllCheckoutCartInstances);
      window.upcartOnAddToCart = wrapCallback(originalAddToCart, syncAllCheckoutCartInstances);
      window.upcartOnItemRemoved = wrapCallback(originalItemRemoved, syncAllCheckoutCartInstances);
      window.upcartSelectUpsellItemSellingPlan = wrapCallback(originalSelectUpsellItemSellingPlan, syncAllCheckoutCartInstances);
  }, 500);
});
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

function cleanProductName(name) {
    if (!name) return name;
  return PRODUCT_NAME_PATTERNS.reduce((cleaned, pattern) => cleaned.replace(pattern, ''), name).trim();
}

function getLoopProductData(productId) {
    if (!window.loopProps || !productId) return null;
    const keys = [productId];
    if (typeof productId === 'string') {
      const parsed = parseInt(productId, 10);
      if (!isNaN(parsed)) keys.push(parsed);
    } else if (typeof productId === 'number') {
      keys.push(productId.toString());
    }
  return keys.find(key => window.loopProps[key]?.product) 
    ? window.loopProps[keys.find(key => window.loopProps[key]?.product)].product 
    : null;
}

async function fetchProductJson(productHandle, fallbackIdentifier) {
    const identifier = productHandle || fallbackIdentifier;
    if (!identifier) return null;
    try {
      const response = await fetch(`${Shopify.routes.root}products/${identifier}.js`, { cache: 'no-store' });
      return response.ok ? JSON.parse(await response.text()) : null;
    } catch (error) {
      console.error('Error fetching product JSON:', error);
      return null;
    }
}
  
function inferProductHandle(itemData) {
    if (itemData?.productHandle) return itemData.productHandle;
    
    const selectors = [];
    if (itemData?.variantId) selectors.push(`[data-variant-id="${itemData.variantId}"]`);
    if (itemData?.productId) selectors.push(`[data-product-id="${itemData.productId}"]`);
    
    for (const selector of selectors) {
      const element = document.querySelector(selector);
      if (element) {
        const handle = element.getAttribute('data-product-handle') ||
                      element.closest('[data-product-handle]')?.getAttribute('data-product-handle') ||
                      element.querySelector('[data-product-handle]')?.getAttribute('data-product-handle');
        if (handle) return handle;
      }
    }
    return null;
}

function findCartId() {
  const cartElement = document.querySelector('[class*="checkout-cart-sticky-"]');
  const match = cartElement?.className.match(/checkout-cart-sticky-([a-zA-Z0-9_-]+)/);
  return match ? match[1] : null;
}

function isMobile() {
  return window.innerWidth <= CART_CONSTANTS.MOBILE_BREAKPOINT;
}

function getCartEndpoint() {
  const cartEndpointBase = window.Shopify?.routes?.root || '/';
  return cartEndpointBase.endsWith('/') ? `${cartEndpointBase}cart.js` : `${cartEndpointBase}/cart.js`;
}

// ============================================================================
// CART CALCULATION FUNCTIONS
// ============================================================================

function calculateCartTotal(cartItems) {
  return cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
}

function calculateCartTotalForDiscount(cartItems) {
  return cartItems.reduce((sum, item) => 
    item.isSubscription ? sum : sum + (item.price * item.quantity), 0);
}

function hasNonSubscriptionItems(cartItems) {
  return cartItems.some(item => !item.isSubscription);
}

function calculateFinalPrice(cartItems, purchaseOption, subscribeDiscount, tryOnceDiscount) {
  const baseTotal = calculateCartTotal(cartItems);
  const discount = purchaseOption === 'subscribe' ? subscribeDiscount : 
                   purchaseOption === 'try-once' ? tryOnceDiscount : 0;
  return baseTotal * (1 - discount / 100);
}

function calculateProgressBarWidth(progressBarElement) {
  if (!progressBarElement) {
    return isMobile() ? window.innerWidth * 0.85 : 400;
  }
  return progressBarElement.clientWidth || progressBarElement.offsetWidth || 
         progressBarElement.getBoundingClientRect().width || 
         (isMobile() ? window.innerWidth * 0.85 : 400);
}

function calculateProgressFillWidth(cartTotal, maxValue, progressBarWidth) {
  const fillWidthPixels = Math.min((cartTotal / maxValue) * progressBarWidth, progressBarWidth);
  return progressBarWidth > 0 ? (fillWidthPixels / progressBarWidth) * 100 : 0;
}

  // ============================================================================
// CART STATE MANAGEMENT FUNCTIONS
  // ============================================================================
  
function createCartState(cartId) {
  return {
    cartId,
    currentCartTotal: 0,
    cartItems: [],
    isInitialized: false,
    isInitialSync: true,
    hasSyncedBefore: false,
    currentPurchaseOption: 'subscribe',
    subscribeDiscountPercentage: CART_CONSTANTS.DEFAULT_SUBSCRIBE_DISCOUNT,
    tryOnceDiscountPercentage: CART_CONSTANTS.DEFAULT_TRY_ONCE_DISCOUNT,
    productSellingPlansCache: {},
    isPurchaseOptionMutationInFlight: false,
    outstandingAlignmentPromise: null,
    alignmentQueued: false,
    shopifyCartTotal: 0,
    shopifyCartOriginalTotal: 0,
    originalEmptySlots: null,
    cachedTierElements: null,
    lastScrollY: window.scrollY,
    scrollAdjustmentTimeout: null,
    touchStartY: 0,
    touchStartTime: 0,
    isDragging: false
  };
}

function getCartElements(cartId) {
  return {
    stickyCartElement: document.querySelector('.checkout-cart-sticky-' + cartId),
    fixedCartElement: document.querySelector('.checkout-cart-fixed-' + cartId),
    fixedCartCheckoutButton: document.querySelector('.checkout-cart-fixed-' + cartId)?.querySelector('.btn-checkout-' + cartId),
    defaultFixedCheckoutHref: document.querySelector('.checkout-cart-fixed-' + cartId)?.querySelector('.btn-checkout-' + cartId)?.getAttribute('href')
  };
}

// ============================================================================
// DOM MANIPULATION FUNCTIONS
// ============================================================================

function initializeEmptySlots(cartId, stickyCartElement) {
  const cartBodyElement = stickyCartElement?.querySelector('.checkout-cart-body-' + cartId) || 
                          document.querySelector('.checkout-cart-body-' + cartId);
  if (!cartBodyElement) return [];
  
  const allItemElements = Array.from(cartBodyElement.querySelectorAll(`.checkout-cart-item-${cartId}`));
  return allItemElements.filter(el => {
    const productName = el.querySelector(`.product-name-${cartId}`);
    const hasSvgPlaceholder = productName && productName.querySelector('svg');
    return hasSvgPlaceholder || el.style.visibility === 'hidden' || el.innerHTML.trim() === '';
  }).map(el => el.cloneNode(true));
}

function updateToggleButtons(cartId, isCollapsed) {
  const arrow = isCollapsed ? window.checkoutCartToggleArrowUp : window.checkoutCartToggleArrowDown;
  document.querySelectorAll(`.checkout-cart-sticky-${cartId} .cart-toggle-${cartId}`).forEach(btn => btn.innerHTML = arrow);
  document.querySelectorAll(`.checkout-cart-fixed-${cartId} .cart-toggle-${cartId}`).forEach(btn => btn.innerHTML = window.checkoutCartToggleArrowUp);
}

function updateFixedCartVisibility(cartId, state, elements) {
  const { fixedCartElement, stickyCartElement } = elements;
  if (!fixedCartElement) return;

  if (isMobile()) {
      fixedCartElement.classList.remove('is-visible');
      return;
    }

    if (!stickyCartElement) {
      fixedCartElement.classList.add('is-visible');
      return;
    }

    const rect = stickyCartElement.getBoundingClientRect();
    const stickyBottomAboveViewport = rect.bottom <= 0;

    if (stickyBottomAboveViewport) {
      fixedCartElement.classList.add('is-visible');
    } else {
      fixedCartElement.classList.remove('is-visible');
    }
  }
  
function showAddToCartFeedback(cartId, stickyCartElement) {
  if (stickyCartElement) {
    stickyCartElement.classList.add('cart-feedback-highlight');
    setTimeout(() => {
      stickyCartElement.classList.remove('cart-feedback-highlight');
    }, 300);
  }
}

function createSeparator(cartId) {
  const separator = document.createElement('div');
  separator.className = `checkout-cart-item-separator-${cartId}`;
  return separator;
}

function createCartItemElement(cartId, item, index, state, callbacks) {
  const itemDiv = document.createElement('div');
  itemDiv.className = `checkout-cart-item-${cartId}`;
  
  const priceDisplayClass = item.isSubscription
    ? `price-pill-${cartId} price-pill-subscribe-${cartId}`
    : `price-pill-${cartId} price-pill-onetime-${cartId}`;
  const priceDisplay = `<span class="${priceDisplayClass}">$${item.price.toFixed(2)} each</span>`;
  
  const showFrequencySelector = state.currentPurchaseOption === 'subscribe';
  const frequencySelectorHtml = showFrequencySelector ? `
    <div class="delivery-frequency-selector-item-${cartId}" data-variant-id="${item.variantId}" data-product-id="${item.productId}">
      <div class="delivery-frequency-container-item-${cartId}">
        <div class="show-selector">
          <span class="selector-image">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 18 18" height="18" width="18">
              <path stroke-linejoin="round" stroke-linecap="round" stroke="#666666" d="M6 1.5V4.5"></path>
              <path stroke-linejoin="round" stroke-linecap="round" stroke="#666666" d="M12 1.5V4.5"></path>
              <path stroke-linejoin="round" stroke-linecap="round" stroke="#666666" d="M3.75 3H14.25C14.25 3 15.75 3 15.75 4.5V15C15.75 15 15.75 16.5 14.25 16.5H3.75C3.75 16.5 2.25 16.5 2.25 15V4.5C2.25 4.5 2.25 3 3.75 3Z"></path>
              <path stroke-linejoin="round" stroke-linecap="round" stroke="#666666" d="M2.25 7.5H15.75"></path>
            </svg>
          </span>
          <span class="serving-count-label">
            <span class="serving-description">Every</span>
            <span class="serving-count days-count">30</span>
            <span class="serving-description">days</span>
          </span>
        </div>
        <select class="cart-delivery-frequency-select-${cartId}" data-variant-id="${item.variantId}" data-product-id="${item.productId}">
          <option value="">Loading...</option>
        </select>
      </div>
    </div>
  ` : '';
  
  itemDiv.innerHTML = `
    <div class="product-img-${cartId}">
      ${item.image ? `<img src="${item.image}" width="50" height="50" alt="${item.name}">` : DEFAULT_SVGS.defaultProductImage}
    </div>
    <div class="product-info-${cartId}">
      <div class="product-name-${cartId}">
        ${item.name}
      </div>
      <div class="product-quantity-container-${cartId}">
        <div class="cart-quantity-selector-widget-${cartId}">
          <button class="cart-quantity-btn cart-quantity-btn-minus-${cartId}" data-action="decrease" data-variant-id="${item.variantId}">
            <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 9 2" fill="none">
              <path d="M0 0.605225H8.23137" stroke="white" stroke-width="1.21049"/>
            </svg>
          </button>
          <input type="number" class="cart-quantity-display-${cartId}" data-variant-id="${item.variantId}" value="${item.quantity}" min="1" max="999">
          <button class="cart-quantity-btn cart-quantity-btn-plus-${cartId}" data-action="increase" data-variant-id="${item.variantId}">
            <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 9 9" fill="none">
              <path d="M4.47772 0L4.47772 8.95544M0 4.47772H8.95544" stroke="white" stroke-width="1.31698"/>
            </svg>
          </button>
        </div>
        ${frequencySelectorHtml}
      </div>
    </div>
    <div class="btn-remove-${cartId}" onclick="removeItemFromCartSnippet('${item.variantId}')">
      <svg width="24" height="24" viewBox="0 0 32 31" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="15.9785" cy="15.512" r="15.4062" fill="#D3D3D3"></circle>
        <path d="M9.16429 8.1438L23.9007 22.8802M23.3458 8.1438L8.60938 22.8802" stroke="white" stroke-width="2"></path>
      </svg>
    </div>
  `;
  
  callbacks.setupCartQuantitySelector(itemDiv, item);
  
  if (showFrequencySelector) {
    callbacks.setupDeliveryFrequencySelector(itemDiv, item);
  }
  
  return itemDiv;
}

function updateDiscountBadgeStates(cartId, cartTotalForDiscount, currentCartTotal) {
  const tierElements = document.querySelectorAll(`[id^="tier-"][id$="-${cartId}"]`);
  if (tierElements.length === 0) return;
  
  const totalForTier = cartTotalForDiscount !== undefined ? cartTotalForDiscount : currentCartTotal;
  
  console.log(`DEBUG: Updating badge states for cart total: $${totalForTier} (excluding subscriptions)`);
  
  tierElements.forEach((tierElement, index) => {
    const threshold = parseFloat(tierElement.getAttribute('data-threshold')) || 0;
    
    if (totalForTier >= threshold) {
      tierElement.className = `discount-tier-${cartId} discount-tier-active-${cartId}`;
      console.log(`DEBUG: Badge ${index} ($${threshold}) - ACTIVE`);
      } else {
      tierElement.className = `discount-tier-${cartId} discount-tier-inactive-${cartId}`;
      console.log(`DEBUG: Badge ${index} ($${threshold}) - INACTIVE`);
    }
  });
}

function getCurrentDiscountTier(cartId, cartTotalForDiscount, currentCartTotal, cachedTierElements) {
  let tierElements = cachedTierElements;
  if (!tierElements) {
    tierElements = document.querySelectorAll(`[id^="tier-"][id$="-${cartId}"]`);
  }
  
  let currentTier = { discount: 0, label: '0% OFF' };
  const totalForTier = cartTotalForDiscount !== undefined ? cartTotalForDiscount : currentCartTotal;
  
  tierElements.forEach(tierElement => {
    const threshold = parseFloat(tierElement.getAttribute('data-threshold')) || 0;
    const percentageText = tierElement.textContent.trim();
    const percentage = parseFloat(percentageText.replace('% OFF', '')) || 0;
    
    if (totalForTier >= threshold && percentage > currentTier.discount) {
      currentTier = { discount: percentage, label: percentageText };
    }
  });
  
  return { tier: currentTier, cachedElements: tierElements };
}

function updateProgressBar(cartId, progressCartTotal, discountTiersContainer, progressBarElement, progressFillElement) {
  const maxValue = parseFloat(discountTiersContainer?.getAttribute('data-max-value')) || CART_CONSTANTS.DEFAULT_MAX_PROGRESS_VALUE;
  const progressBarWidth = calculateProgressBarWidth(progressBarElement);
  const progressFillWidth = calculateProgressFillWidth(progressCartTotal, maxValue, progressBarWidth);

    if (progressFillElement) {
      progressFillElement.style.width = progressFillWidth + '%';
    }
}

function updateProgressMessage(cartId, progressCartTotal, stickyCartElement) {
  const queryElement = (selector) => stickyCartElement?.querySelector(selector) || document.querySelector(selector);
    const progressMsg = queryElement('.progress-msg-' + cartId);
  
    if (progressMsg) {
    const freeShippingThreshold = parseFloat(progressMsg.getAttribute('data-free-shipping-threshold')) || CART_CONSTANTS.DEFAULT_FREE_SHIPPING_THRESHOLD;
      const remaining = Math.max(0, freeShippingThreshold - progressCartTotal);
      progressMsg.textContent = remaining > 0 
        ? `Add $${remaining.toFixed(2)} more to get Free Shipping`
        : 'You qualify for Free Shipping!';
  }
    }

function updateSummaryElements(elements, options, cartItems, totalBags, bagCountText, basePriceValue, finalPriceValue, showDiscountPricing, isDesktop) {
      if (!elements) return;
  
      const {
        bagCount,
        priceContainer,
        basePrice,
        currentPrice,
        checkoutButton
      } = elements;

      const {
        hideWhenEmptyOnDesktop = false,
        buttonTextWhenEmpty,
        buttonTextWhenItems,
        disableButtonWhenEmpty = false,
        defaultHref,
        hrefWhenItems
      } = options;

      if (bagCount) {
        bagCount.textContent = bagCountText;
        if (hideWhenEmptyOnDesktop && isDesktop) {
          bagCount.style.visibility = cartItems.length === 0 ? 'hidden' : 'visible';
        } else if (hideWhenEmptyOnDesktop) {
          bagCount.style.visibility = '';
        }
      }

      if (priceContainer) {
        if (hideWhenEmptyOnDesktop && isDesktop) {
          priceContainer.style.visibility = cartItems.length === 0 ? 'hidden' : 'visible';
        } else if (hideWhenEmptyOnDesktop) {
          priceContainer.style.visibility = '';
        }
      }

      if (basePrice && currentPrice) {
        if (!showDiscountPricing) {
          basePrice.style.display = 'none';
          currentPrice.textContent = basePriceValue;
          currentPrice.style.color = cartItems.length === 0 ? '#7C7C7C' : '#2BA059';
          currentPrice.style.textDecoration = '';
        } else {
          basePrice.style.display = '';
          basePrice.textContent = basePriceValue;
          currentPrice.textContent = finalPriceValue;
          currentPrice.style.color = '#2BA059';
          currentPrice.style.textDecoration = '';
        }
      }

      if (checkoutButton) {
        checkoutButton.dataset.hasItems = totalBags > 0 ? 'true' : 'false';

        if (buttonTextWhenEmpty) {
          checkoutButton.textContent = totalBags === 0 ? buttonTextWhenEmpty : (buttonTextWhenItems || buttonTextWhenEmpty);
        }

        if (disableButtonWhenEmpty && checkoutButton instanceof HTMLButtonElement) {
          checkoutButton.disabled = totalBags === 0;
        }

        if (checkoutButton instanceof HTMLAnchorElement) {
          if (totalBags === 0) {
            if (defaultHref !== null && defaultHref !== undefined) {
              checkoutButton.setAttribute('href', defaultHref);
            } else {
              checkoutButton.removeAttribute('href');
            }
          } else if (hrefWhenItems) {
            checkoutButton.setAttribute('href', hrefWhenItems);
          }
        }
      }
}

function updateCartDisplay(cartId, state, elements, callbacks) {
  if (!state.isInitialized) return;

  state.currentCartTotal = calculateCartTotal(state.cartItems);
  const discountCartTotal = calculateCartTotalForDiscount(state.cartItems);
  const progressCartTotal = state.currentPurchaseOption === 'subscribe' ? state.currentCartTotal : discountCartTotal;
  
  // Skip progress bar updates during purchase option mutation to prevent multiple movements
  const skipProgressBarUpdate = state.isPurchaseOptionMutationInFlight;
  
  const queryElement = (selector) => elements.stickyCartElement?.querySelector(selector) || document.querySelector(selector);
  
  const progressFillElement = document.getElementById('progress-fill-' + cartId);
  const discountTiersContainer = queryElement('.discount-tiers-' + cartId);
  const cartBodyElement = queryElement('.checkout-cart-body-' + cartId);
  const progressBarElement = queryElement(`.progress-bar-${cartId}`);

  const stickySummaryElements = elements.stickyCartElement ? {
    bagCount: elements.stickyCartElement.querySelector('.bag-count-' + cartId),
    priceContainer: elements.stickyCartElement.querySelector('.price-container-' + cartId),
    basePrice: elements.stickyCartElement.querySelector('.base-price-' + cartId),
    currentPrice: elements.stickyCartElement.querySelector('.current-price-' + cartId),
    checkoutButton: elements.stickyCartElement.querySelector('.btn-checkout-' + cartId)
  } : {};

  const fixedSummaryElements = elements.fixedCartElement ? {
    bagCount: elements.fixedCartElement.querySelector('.bag-count-' + cartId),
    priceContainer: elements.fixedCartElement.querySelector('.price-container-' + cartId),
    basePrice: elements.fixedCartElement.querySelector('.base-price-' + cartId),
    currentPrice: elements.fixedCartElement.querySelector('.current-price-' + cartId),
    checkoutButton: elements.fixedCartElement.querySelector('.btn-checkout-' + cartId)
  } : {};

  const totalBags = state.cartItems.reduce((total, item) => total + item.quantity, 0);
  
  const { tier: currentTier, cachedElements } = getCurrentDiscountTier(
    cartId, 
    progressCartTotal, 
    state.currentCartTotal, 
    state.cachedTierElements
  );
  state.cachedTierElements = cachedElements;
  
  const finalPrice = state.shopifyCartTotal || 0;
  const displayBaseTotal = state.shopifyCartOriginalTotal;
  
  const isDesktop = !isMobile();
  const bagCountText = totalBags === 0 ? 'Start adding to save and get sipping!' : `Total of ${totalBags} bags`;
  
  const basePriceValue = `$${displayBaseTotal.toFixed(2)}`;
  const finalPriceValue = `$${finalPrice.toFixed(2)}`;
  const showDiscountPricing = state.cartItems.length > 0 && Math.abs(displayBaseTotal - finalPrice) > 0.01;

  if (elements.stickyCartElement) {
    if (state.cartItems.length === 0) {
      elements.stickyCartElement.classList.add('cart-empty');
      } else {
      elements.stickyCartElement.classList.remove('cart-empty');
    }
  }

  if (cartBodyElement) {
    if (state.originalEmptySlots === null) {
      state.originalEmptySlots = initializeEmptySlots(cartId, elements.stickyCartElement);
    }
    const emptySlots = state.originalEmptySlots || [];
    
    cartBodyElement.innerHTML = '';
    
    state.cartItems.forEach((item, index) => {
      cartBodyElement.appendChild(createCartItemElement(cartId, item, index, state, callbacks));
      if (index < state.cartItems.length - 1 && index < 2) {
        cartBodyElement.appendChild(createSeparator(cartId));
      }
    });

    const remainingSlots = Math.max(0, 2 - state.cartItems.length);
    for (let i = 0; i < remainingSlots; i++) {
      if (state.cartItems.length > 0 && i === 0) {
        cartBodyElement.appendChild(createSeparator(cartId));
      }
      if (emptySlots.length > i) {
        const emptySlot = emptySlots[i].cloneNode(true);
        emptySlot.style.display = '';
        emptySlot.style.visibility = '';
        cartBodyElement.appendChild(emptySlot);
        if (i < remainingSlots - 1) {
          cartBodyElement.appendChild(createSeparator(cartId));
        }
      }
    }
  }

  // Only update progress bar if not during purchase option mutation
  if (!skipProgressBarUpdate) {
    updateProgressBar(cartId, progressCartTotal, discountTiersContainer, progressBarElement, progressFillElement);
    updateDiscountBadgeStates(cartId, progressCartTotal, state.currentCartTotal);
    updateProgressMessage(cartId, progressCartTotal, elements.stickyCartElement);
  }

  const bagCountMsgText = queryElement('.bag-count-msg-text-' + cartId);
  if (bagCountMsgText) {
    bagCountMsgText.textContent = `Total of ${totalBags} bags`;
  }

  updateSummaryElements(stickySummaryElements, {
    hideWhenEmptyOnDesktop: true,
    buttonTextWhenEmpty: 'Add to cart',
    buttonTextWhenItems: 'Checkout',
    disableButtonWhenEmpty: true
  }, state.cartItems, totalBags, bagCountText, basePriceValue, finalPriceValue, showDiscountPricing, isDesktop);

  updateSummaryElements(fixedSummaryElements, {
    hideWhenEmptyOnDesktop: false,
    buttonTextWhenEmpty: 'Build Your Cart',
    buttonTextWhenItems: 'Complete Checkout',
    defaultHref: elements.defaultFixedCheckoutHref,
    hrefWhenItems: '#complete-checkout'
  }, state.cartItems, totalBags, bagCountText, basePriceValue, finalPriceValue, showDiscountPricing, isDesktop);

  updateFixedCartVisibility(cartId, state, elements);
}

// ============================================================================
// MOBILE SCROLL AND TOUCH HANDLERS
// ============================================================================

function handleMobileScrollAdjustment(cartId, state) {
  if (!isMobile()) return;
  
  const cartElement = document.querySelector('.checkout-cart-sticky-' + cartId);
  if (!cartElement) return;
  
  const currentScrollY = window.scrollY;
  const scrollDelta = currentScrollY - state.lastScrollY;
  const isScrollAdjusted = cartElement.classList.contains('scroll-adjusted');
  
  if (state.scrollAdjustmentTimeout) clearTimeout(state.scrollAdjustmentTimeout);
  
  if (scrollDelta > 0 && !isScrollAdjusted) {
    cartElement.classList.add('scroll-adjusted');
  } else if (scrollDelta < 0) {
    state.scrollAdjustmentTimeout = setTimeout(() => {
      cartElement.classList.remove('scroll-adjusted');
    }, 500);
  }
  
  state.lastScrollY = currentScrollY;
}

function createTouchHandlers(cartId, state, callbacks) {
  function handleTouchStart(e) {
    if (!isMobile()) return;
    state.touchStartY = e.touches[0].clientY;
    state.touchStartTime = Date.now();
    state.isDragging = false;
  }
  
  function handleTouchMove(e) {
    if (!isMobile() || !state.touchStartY) return;
    
    const touchCurrentY = e.touches[0].clientY;
    const deltaY = touchCurrentY - state.touchStartY;
    
    if (Math.abs(deltaY) > 10) {
      state.isDragging = true;
      e.preventDefault();
      
      const cartElement = document.querySelector('.checkout-cart-sticky-' + cartId);
      if (cartElement) {
        const feedbackAmount = Math.min(Math.abs(deltaY) * 0.1, CART_CONSTANTS.TOUCH_FEEDBACK_MAX);
        if (deltaY < 0) {
          cartElement.style.transform = `translateY(-${feedbackAmount}px)`;
        } else {
          cartElement.style.transform = `translateY(${feedbackAmount}px)`;
        }
      }
    }
  }
  
  function handleTouchEnd(e) {
    if (!isMobile() || !state.touchStartY || !state.isDragging) {
      state.touchStartY = 0;
        return;
      }
      
    const touchEndY = e.changedTouches[0].clientY;
    const deltaY = touchEndY - state.touchStartY;
    const deltaTime = Date.now() - state.touchStartTime;
    const velocity = Math.abs(deltaY) / deltaTime;
    
    const cartElement = document.querySelector('.checkout-cart-sticky-' + cartId);
    const isCollapsed = cartElement && cartElement.classList.contains('cart-collapsed');
    
    const shouldOpen = deltaY < -CART_CONSTANTS.DRAG_THRESHOLD || (deltaY < 0 && velocity > CART_CONSTANTS.VELOCITY_THRESHOLD);
    const shouldClose = deltaY > CART_CONSTANTS.DRAG_THRESHOLD || (deltaY > 0 && velocity > CART_CONSTANTS.VELOCITY_THRESHOLD);
    
    if (shouldOpen && isCollapsed) {
      console.log('Touch drag: Opening cart (swipe up)');
      callbacks.toggleCart(cartId);
    } else if (shouldClose && !isCollapsed) {
      console.log('Touch drag: Closing cart (swipe down)');
      callbacks.toggleCart(cartId);
    }
    
    if (cartElement) {
      cartElement.style.transform = '';
    }
    
    state.touchStartY = 0;
    state.isDragging = false;
  }
  
  return { handleTouchStart, handleTouchMove, handleTouchEnd };
}

function setupTouchDrag(cartId, touchHandlers) {
  const cartElement = document.querySelector('.progress-section-' + cartId);
  const cartFooter = document.querySelector('.checkout-cart-footer-' + cartId);
  
  if (cartElement && cartFooter) {
    cartElement.addEventListener('touchstart', touchHandlers.handleTouchStart, { passive: false });
    cartFooter.addEventListener('touchstart', touchHandlers.handleTouchStart, { passive: false });
    cartElement.addEventListener('touchmove', touchHandlers.handleTouchMove, { passive: false });
    cartFooter.addEventListener('touchmove', touchHandlers.handleTouchMove, { passive: false });
    cartElement.addEventListener('touchend', touchHandlers.handleTouchEnd, { passive: true });
    cartFooter.addEventListener('touchend', touchHandlers.handleTouchEnd, { passive: true });
    console.log("Touch drag functionality added for mobile cart:", cartId);
  }
}

// ============================================================================
// CART OPERATIONS
// ============================================================================

async function addItemToCart(cartId, itemData, state, elements, callbacks) {
  showAddToCartFeedback(cartId, elements.stickyCartElement);
  setTimeout(() => callbacks.syncWithShopifyCart(), CART_CONSTANTS.SYNC_DELAY);
  
  const isFirstProductAdd = state.cartItems.length === 0;
  if (!state.isInitialSync && isMobile() && isFirstProductAdd) {
    if (elements.stickyCartElement?.classList.contains('cart-collapsed')) {
      elements.stickyCartElement.classList.remove('cart-collapsed');
      updateToggleButtons(cartId, false);
    }
  }
}

async function removeItemFromCart(cartId, variantId, state, elements, callbacks) {
  try {
    const cartData = await fetch('/cart.js', { credentials: 'same-origin' }).then(res => res.json());
    const lineItem = cartData.items.find(i => i.variant_id == variantId);
    
    if (!lineItem) {
      console.warn('Item not found in Shopify cart:', variantId);
      setTimeout(() => callbacks.syncWithShopifyCart(), CART_CONSTANTS.SYNC_DELAY);
      return;
    }
    
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: lineItem.key, quantity: 0 })
    });
    
    if (window.upcartRefreshCart) window.upcartRefreshCart();
    setTimeout(() => callbacks.syncWithShopifyCart(), CART_CONSTANTS.SYNC_DELAY);
    
    setTimeout(() => {
      if (isMobile() && state.cartItems.length === 0) {
        if (elements.stickyCartElement) {
          elements.stickyCartElement.classList.add('cart-collapsed');
          updateToggleButtons(cartId, true);
        }
      }
    }, 300);
    } catch (error) {
    console.error('Error removing item:', error);
    setTimeout(() => callbacks.syncWithShopifyCart(), CART_CONSTANTS.SYNC_DELAY);
    }
  }
  
async function getLineItemForVariant(variantId, state) {
    const cartData = await fetch('/cart.js', { credentials: 'same-origin' }).then(res => res.json());
    const lineItem = cartData.items.find(item => item.variant_id == variantId);
    
    if (lineItem) {
    const localItem = state.cartItems.find(item => item.variantId === variantId);
      if (localItem) {
        localItem.lineItemKey = lineItem.key;
        localItem.quantity = lineItem.quantity;
      }
      return lineItem;
    }
    return null;
}
  
async function updateCartItemSellingPlan(cartId, variantId, sellingPlanId, state, options = {}) {
    const { skipSync = false } = options;
    const isPurchaseOptionChange = state.isPurchaseOptionMutationInFlight;
    
    try {
    const lineItem = await getLineItemForVariant(variantId, state);
      if (!lineItem) {
        console.warn('Line item not found for variant:', variantId);
        return;
      }
      
      const headers = { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
      
      if (sellingPlanId) {
        // Check if line item already has a selling plan
        const hasExistingSellingPlan = lineItem.selling_plan_id != null;
        
        if (hasExistingSellingPlan) {
          // Item already has a selling plan - change it directly
          await fetch('/cart/change.js', {
            method: 'POST', headers, credentials: 'same-origin',
            body: JSON.stringify({ 
              id: lineItem.key, 
              quantity: lineItem.quantity, 
              selling_plan: sellingPlanId 
            })
          });
          
          // Update local state
          const localItem = state.cartItems.find(item => item.variantId === variantId);
          if (localItem) {
            // Line item key stays the same when changing selling plan
            localItem.lineItemKey = lineItem.key;
          }
        } else {
          // Item doesn't have a selling plan - try direct change first, fallback to remove/add
          try {
            const changeResponse = await fetch('/cart/change.js', {
              method: 'POST', headers, credentials: 'same-origin',
              body: JSON.stringify({ 
                id: lineItem.key, 
                quantity: lineItem.quantity, 
                selling_plan: sellingPlanId 
              })
            });
            
            const changeData = await changeResponse.json();
            
            // Check if change was successful
            if (changeData && !changeData.errors && changeData.items) {
              const updatedItem = changeData.items.find(item => item.variant_id == variantId);
              if (updatedItem) {
                // Success - update local state
                const localItem = state.cartItems.find(item => item.variantId === variantId);
                if (localItem && updatedItem.key) {
                  localItem.lineItemKey = updatedItem.key;
                }
              } else {
                throw new Error('Item not found in response');
              }
            } else {
              throw new Error('Change API returned errors');
            }
          } catch (error) {
            // Fallback: remove and re-add if direct change is not supported
            console.log('Direct selling plan change not supported, using remove/add method');
            
            await fetch('/cart/change.js', {
              method: 'POST', headers, credentials: 'same-origin',
              body: JSON.stringify({ id: lineItem.key, quantity: 0 })
            });
            
            const addData = await fetch('/cart/add.js', {
              method: 'POST', headers, credentials: 'same-origin',
              body: JSON.stringify({
                form_type: 'product', id: variantId, loop_purchase_option: 'on',
                loop_variant_id: variantId, 'product-id': lineItem.product_id || variantId,
                quantity: lineItem.quantity, selling_plan: sellingPlanId, utf8: '✓'
              })
            }).then(res => res.json());
            
            const localItem = state.cartItems.find(item => item.variantId === variantId);
            if (localItem && addData?.key) localItem.lineItemKey = addData.key;
          }
        }
      } else {
        // Remove selling plan (one-time purchase)
        await fetch('/cart/change.js', {
          method: 'POST', headers, credentials: 'same-origin',
          body: JSON.stringify({ id: lineItem.key, quantity: lineItem.quantity, selling_plan: null })
        });
      }
      
      // Only call upcartRefreshCart if not during purchase option change
      // if (!isPurchaseOptionChange && window.upcartRefreshCart) {
        window.upcartRefreshCart();
      // }
      
      // Never sync during purchase option change - sync will happen once at the end
      if (!skipSync && !isPurchaseOptionChange) {
      setTimeout(() => {
        const syncFn = window['syncWithShopifyCart_' + cartId];
        if (syncFn) syncFn();
      }, CART_CONSTANTS.SYNC_DELAY);
      }
    } catch (error) {
      console.error('Error updating selling plan:', error);
      throw error;
    }
  }
  
function updateCartItemQuantity(cartId, variantId, newQuantity, itemData, callbacks) {
  console.log('Item quantity updated in Shopify cart, syncing checkout cart...');
  setTimeout(() => callbacks.syncWithShopifyCart(), CART_CONSTANTS.SYNC_DELAY);
}

function updateShopifyCartQuantity(cartId, variantId, newQuantity, sellingPlan, state, callbacks) {
  function updateCart(lineItem) {
        const payload = lineItem 
          ? { id: lineItem.key, quantity: newQuantity }
          : { id: variantId, quantity: newQuantity, ...(sellingPlan && { selling_plan: sellingPlan }) };
        
        const endpoint = lineItem ? '/cart/change.js' : '/cart/add.js';
        
        fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(payload)
        })
        .then(() => {
          if (window.upcartRefreshCart) window.upcartRefreshCart();
      setTimeout(() => callbacks.syncWithShopifyCart(), CART_CONSTANTS.SYNC_DELAY);
        })
        .catch(error => console.error('Error updating Shopify cart:', error));
  }
      
      fetch('/cart.js')
        .then(res => res.json())
        .then(cartData => {
          const lineItem = cartData.items.find(item => item.variant_id == variantId);
      const localItem = state.cartItems.find(item => item.variantId === variantId);
          if (localItem && lineItem) {
            localItem.lineItemKey = lineItem.key;
            localItem.quantity = lineItem.quantity;
          }
          updateCart(lineItem);
        })
        .catch(error => console.error('Error fetching cart:', error));
    }
    
function updateProductTileQuantitySelector(variantId, newQuantity) {
  const variantElement = document.querySelector(`[data-variant-id="${variantId}"]`);
  const tile = variantElement?.closest('.grid-product-tile-container');
  const input = tile?.querySelector(`.button-container-${variantId}, .product-tile-actions-${variantId}`)
    ?.querySelector('.quantity-selector-widget .quantity-display');
  if (input) input.value = newQuantity;
}

function setupCartQuantitySelector(cartId, itemElement, item, state, callbacks) {
  const variantId = item.variantId;
  const selector = `[data-variant-id="${variantId}"]`;
  const quantityInput = itemElement.querySelector(`.cart-quantity-display-${cartId}${selector}`);
  const minusBtn = itemElement.querySelector(`.cart-quantity-btn-minus-${cartId}${selector}`);
  const plusBtn = itemElement.querySelector(`.cart-quantity-btn-plus-${cartId}${selector}`);
  
  if (!quantityInput || !minusBtn || !plusBtn) return;
  
  const getCurrentQuantity = () => parseInt(quantityInput.value) || 1;
  
  const updateQuantity = (newQuantity) => {
    if (newQuantity < 1) {
      callbacks.removeItemFromCart(variantId);
      return;
    }
    
    if (newQuantity !== getCurrentQuantity()) {
      quantityInput.value = newQuantity;
      updateCartItemQuantity(cartId, variantId, newQuantity, { ...item, quantity: newQuantity }, callbacks);
      updateShopifyCartQuantity(cartId, variantId, newQuantity, item.sellingPlan, state, callbacks);
      updateProductTileQuantitySelector(variantId, newQuantity);
    }
  };
  
    minusBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
    updateQuantity(getCurrentQuantity() - 1);
    });
    
    plusBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
    updateQuantity(getCurrentQuantity() + 1);
    });
    
    quantityInput.addEventListener('input', (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      const inputValue = parseInt(e.target.value) || 0;
      
    if (inputValue < CART_CONSTANTS.QUANTITY_MIN) {
      quantityInput.value = CART_CONSTANTS.QUANTITY_MIN;
      updateQuantity(CART_CONSTANTS.QUANTITY_MIN);
    } else if (inputValue > CART_CONSTANTS.QUANTITY_MAX) {
      quantityInput.value = CART_CONSTANTS.QUANTITY_MAX;
      updateQuantity(CART_CONSTANTS.QUANTITY_MAX);
      } else {
        updateQuantity(inputValue);
      }
    });
    
    quantityInput.addEventListener('blur', (e) => {
      const inputValue = parseInt(e.target.value) || 1;
    if (inputValue < CART_CONSTANTS.QUANTITY_MIN) {
      quantityInput.value = CART_CONSTANTS.QUANTITY_MIN;
      updateQuantity(CART_CONSTANTS.QUANTITY_MIN);
    }
  });
  
    quantityInput.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        quantityInput.blur();
      }
    });
    
    quantityInput.style.setProperty('-moz-appearance', 'textfield', 'important');
    quantityInput.style.setProperty('-webkit-appearance', 'none', 'important');
    quantityInput.style.setProperty('appearance', 'none', 'important');
    
    if (!document.getElementById('cart-quantity-input-styles-' + cartId)) {
      const style = document.createElement('style');
      style.id = 'cart-quantity-input-styles-' + cartId;
      style.textContent = `
        .cart-quantity-display-${cartId}::-webkit-outer-spin-button,
        .cart-quantity-display-${cartId}::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        .cart-quantity-display-${cartId} {
          -moz-appearance: textfield;
        }
        .cart-quantity-btn-minus-${cartId}:hover,
        .cart-quantity-btn-plus-${cartId}:hover {
          background-color: #7BB8E8 !important;
        }
        .cart-quantity-btn-minus-${cartId}:active,
        .cart-quantity-btn-plus-${cartId}:active {
          background-color: #6BA5D9 !important;
          transform: scale(0.95);
        }
      `;
      document.head.appendChild(style);
    }
    
    quantityInput.addEventListener('wheel', (e) => {
      e.preventDefault();
    });
  }
  
// ============================================================================
// DELIVERY FREQUENCY SELECTOR
// ============================================================================

async function setupDeliveryFrequencySelector(cartId, itemElement, item, state, callbacks) {
  const selectElement = itemElement.querySelector(`.cart-delivery-frequency-select-${cartId}[data-variant-id="${item.variantId}"]`);
  const displayElement = itemElement.querySelector(`.delivery-frequency-selector-item-${cartId} .show-selector`);
  const daysCountElement = itemElement.querySelector(`.delivery-frequency-selector-item-${cartId} .days-count`);
  
  if (!selectElement || !displayElement) return;
  
  try {
    let product = getLoopProductData(item.productId);
    
    if (!product) {
      product = await fetchProductJson(item.productHandle, item.productHandle || item.productId);
    }
    
    if (!product) {
      selectElement.innerHTML = '<option value="">Subscription data unavailable</option>';
      selectElement.disabled = true;
      return;
    }
    
    let sellingPlans = [];
    if (product.variants && product.variants.length > 0) {
      const variant = product.variants.find(v => v.id == item.variantId);
      if (variant && variant.selling_plan_allocations && variant.selling_plan_allocations.length > 0) {
        const allocations = variant.selling_plan_allocations.filter(alloc => {
          const group = product.selling_plan_groups?.find(g => g.id === alloc.selling_plan_group_id);
          return group && group.app_id === '5284869' && !group.name?.includes('Bundle');
        });
        
        if (allocations.length > 0) {
          const groupId = allocations[0].selling_plan_group_id;
          const group = product.selling_plan_groups.find(g => g.id === groupId);
          
          if (group && group.selling_plans) {
            sellingPlans = group.selling_plans;
          }
        }
      }
    }
    
    selectElement.innerHTML = '';
    if (sellingPlans.length > 0) {
      sellingPlans.forEach((plan, index) => {
        if (index != sellingPlans.length-1) {
          const option = document.createElement('option');
          option.value = plan.id;
          option.textContent = `Every ${plan.options[0]?.value || ''}`;
          option.setAttribute('data-days', plan.options[0]?.value?.replace(/days|jours/gi, '').trim() || '30');
          if (index === 0 || (item.sellingPlan && plan.id == item.sellingPlan)) {
            option.selected = true;
            if (daysCountElement) {
              daysCountElement.textContent = plan.options[0]?.value?.replace(/days|jours/gi, '').trim() || '30';
            }
          }
          selectElement.appendChild(option);
        }
      });
      
      if (item.sellingPlan) {
        selectElement.value = item.sellingPlan;
      }
    } else {
      selectElement.innerHTML = '<option value="">No plans available</option>';
    }
    
    selectElement.addEventListener('change', async function() {
      const selectedOption = this.options[this.selectedIndex];
      const days = selectedOption.getAttribute('data-days') || '30';
      
      if (daysCountElement) {
        daysCountElement.textContent = days;
      }
      
      item.sellingPlan = this.value;
      item.isSubscription = true;
      
      try {
        await callbacks.updateCartItemSellingPlan(item.variantId, this.value);
      } catch (error) {
        console.error('Error updating cart item selling plan from selector:', error);
      }
    });
  } catch (error) {
    console.error('Error setting up delivery frequency selector:', error);
    selectElement.innerHTML = '<option value="">Error loading plans</option>';
  }
}
  
  // ============================================================================
// SHOPIFY CART SYNC
  // ============================================================================

function processShopifyCartData(cartData, state) {
  state.shopifyCartTotal = (cartData.total_price || 0) / 100;
  
  state.cartItems = [];
        const currentVariantIds = [];
        
        const hasSubscriptionItems = cartData.items?.some(item => item.selling_plan_allocation);
        
        if (hasSubscriptionItems) {
          let calculatedOriginalTotal = 0;
          cartData.items.forEach(item => {
            const quantity = item.quantity;
            const originalPrice = item.selling_plan_allocation?.compare_at_price 
              ? (item.selling_plan_allocation.compare_at_price / 100)
              : (item.price / 100);
            calculatedOriginalTotal += originalPrice * quantity;
          });
    state.shopifyCartOriginalTotal = calculatedOriginalTotal;
        } else {
    state.shopifyCartOriginalTotal = (cartData.original_total_price || cartData.total_price || 0) / 100;
        }
        
        if (cartData.items?.length > 0) {
          cartData.items.forEach(item => {
            const variantId = item.variant_id.toString();
            const quantity = item.quantity;
            const originalPrice = item.price / 100;
            const finalPrice = item.final_price ? (item.final_price / 100) : originalPrice;
            const sellingPlan = item.selling_plan_allocation?.selling_plan?.id || null;
            
            currentVariantIds.push(variantId);
            
            const fullProductName = item.variant_title 
              ? `${item.product_title || 'Product'} - ${item.variant_title}`
              : (item.product_title || 'Product');
            
      state.cartItems.push({
              variantId,
              productId: item.product_id.toString(),
              productHandle: item.handle,
              name: cleanProductName(fullProductName),
              image: item.image || '',
              price: finalPrice,
              quantity,
              sellingPlan,
              isSubscription: !!sellingPlan,
              lineItemKey: item.key
            });
            
            if (window.replaceAddToCartWithQuantitySelector) {
              setTimeout(() => {
                window.replaceAddToCartWithQuantitySelector(variantId, item.product_id.toString(), !!sellingPlan, sellingPlan);
                const tile = document.querySelector(`[data-variant-id="${variantId}"]`)?.closest('.grid-product-tile-container');
                const input = tile?.querySelector(`.button-container-${variantId}, .product-tile-actions-${variantId}`)
                  ?.querySelector('.quantity-selector-widget .quantity-display');
                if (input) input.value = quantity;
        }, CART_CONSTANTS.SYNC_DELAY);
            }
          });
        }
        
  return currentVariantIds;
}

function syncWithShopifyCart(cartId, state, elements, callbacks) {
  const previousCartItems = state.cartItems.map(item => ({ variantId: item.variantId, productId: item.productId }));
  if (!state.hasSyncedBefore) state.isInitialSync = true;
  
  const cartEndpoint = getCartEndpoint();

  return fetch(cartEndpoint)
    .then(response => response.json())
    .then(cartData => {
      const currentVariantIds = processShopifyCartData(cartData, state);
      
        const removedItems = previousCartItems.filter(prevItem => 
          !currentVariantIds.includes(prevItem.variantId)
        );
        
        if (removedItems.length > 0 && window.replaceQuantitySelectorWithAddToCart) {
          removedItems.forEach(item => {
            setTimeout(() => window.replaceQuantitySelectorWithAddToCart(item.variantId, item.productId), 150);
          });
        }
        
      if (state.isInitialized) {
        callbacks.updateCartDisplay();
      }
      
      // Don't trigger alignment during purchase option mutation - it's already in progress
      if (!state.isPurchaseOptionMutationInFlight) {
        const shouldSubscribe = state.currentPurchaseOption === 'subscribe';
        const needsAlignment = state.cartItems.some(item => shouldSubscribe ? !item.isSubscription : item.isSubscription);
          if (needsAlignment) {
          callbacks.updateCartItemsForPurchaseOption({ syncAfter: false }).catch(error => {
              console.error('Error aligning cart items with purchase option:', error);
            });
          }
        }
      
      const wasInitialSync = state.isInitialSync;
      state.isInitialSync = false;
      state.hasSyncedBefore = true;
      
      if (wasInitialSync && isMobile() && elements.stickyCartElement) {
        if (elements.stickyCartElement.classList.contains('cart-collapsed')) {
          updateToggleButtons(cartId, true);
            console.log('Cart kept collapsed on mobile after initial sync');
          } else {
            console.log('Cart is open after sync, keeping it open');
          }
        }
      })
      .catch(error => {
        console.error('Error fetching Shopify cart:', error);
      const wasInitialSync = state.isInitialSync;
      state.isInitialSync = false;
      state.hasSyncedBefore = true;
      
      if (wasInitialSync && isMobile() && elements.stickyCartElement?.classList.contains('cart-collapsed')) {
        updateToggleButtons(cartId, true);
        }
        return Promise.reject(error);
      });
  }
  
// ============================================================================
// PURCHASE OPTION HANDLERS
// ============================================================================

async function fetchProductSellingPlans(productId, variantId, productHandle, state) {
  const cacheKey = `${productId}_${variantId}`;
  if (state.productSellingPlansCache[cacheKey]) return state.productSellingPlansCache[cacheKey];
  
  try {
    let product = getLoopProductData(productId) || await fetchProductJson(productHandle, productHandle || productId);
    if (!product?.variants) return null;
    
    const variant = product.variants.find(v => v.id == variantId);
    const allocation = variant?.selling_plan_allocations?.find(
      alloc => alloc.selling_plan_group_id && 
      !product.selling_plan_groups?.find(g => g.id === alloc.selling_plan_group_id && g.name?.includes('Bundle'))
    );
    
    if (allocation?.selling_plan_id) {
      state.productSellingPlansCache[cacheKey] = allocation.selling_plan_id;
      return allocation.selling_plan_id;
    }
  } catch (error) {
    console.error('Error fetching selling plans:', error);
  }
  return null;
}

function showLoadingOverlay(cartId) {
  const overlay = document.getElementById('checkout-cart-loading-overlay-' + cartId);
  if (overlay) {
    overlay.classList.add('is-visible');
  }
}

function hideLoadingOverlay(cartId) {
  const overlay = document.getElementById('checkout-cart-loading-overlay-' + cartId);
  if (overlay) {
    overlay.classList.remove('is-visible');
  }
}

async function updateCartItemsForPurchaseOption(cartId, state, options = {}, callbacks) {
  const { syncAfter = true } = options;
  
  if (state.isPurchaseOptionMutationInFlight || state.outstandingAlignmentPromise) {
    state.alignmentQueued = true;
    return;
  }
  
  state.isPurchaseOptionMutationInFlight = true;
  state.outstandingAlignmentPromise = Promise.resolve();
  // showLoadingOverlay(cartId);
  
  try {
    if (state.cartItems.length === 0) {
      callbacks.updateCartDisplay();
      state.isPurchaseOptionMutationInFlight = false;
      state.outstandingAlignmentPromise = null;
      hideLoadingOverlay(cartId);
      return;
    }
    
    const shouldSubscribe = state.currentPurchaseOption === 'subscribe';
    
    // Prepare all items that need updates
    const itemsToUpdate = [];
    const sellingPlanPromises = [];
    
    for (const item of state.cartItems) {
      if (shouldSubscribe) {
        if (item.isSubscription && item.sellingPlan) continue;
        // Fetch selling plans in parallel
        if (!item.sellingPlan) {
          sellingPlanPromises.push(
            fetchProductSellingPlans(item.productId, item.variantId, item.productHandle, state)
              .then(sellingPlanId => ({ item, sellingPlanId }))
          );
        } else {
          itemsToUpdate.push({ item, sellingPlanId: item.sellingPlan });
        }
      } else {
        if (!item.isSubscription && !item.sellingPlan) continue;
        itemsToUpdate.push({ item, sellingPlanId: null });
      }
    }
    
    // Wait for all selling plan fetches to complete in parallel
    if (sellingPlanPromises.length > 0) {
      const fetchedPlans = await Promise.all(sellingPlanPromises);
      fetchedPlans.forEach(({ item, sellingPlanId }) => {
        if (sellingPlanId) {
          itemsToUpdate.push({ item, sellingPlanId });
        } else {
          console.warn('No selling plan found for variant:', item.variantId);
        }
      });
    }
    
    // Update all items in parallel
    const updatePromises = itemsToUpdate.map(({ item, sellingPlanId }) => {
      item.sellingPlan = sellingPlanId;
      item.isSubscription = shouldSubscribe;
      return callbacks.updateCartItemSellingPlan(item.variantId, sellingPlanId, { syncAfter: false, skipSync: true })
        .catch(error => {
          console.error(`Failed to ${shouldSubscribe ? 'set' : 'remove'} subscription for variant ${item.variantId}:`, error);
        });
    });
    
    // Wait for all updates to complete in parallel
    await Promise.all(updatePromises);
    
    // All selling plan changes are complete, now sync once from Shopify cart
    if (syncAfter) {
      // Small delay to ensure Shopify cart has processed all changes
      await new Promise(resolve => setTimeout(resolve, 200));
      
      await callbacks.syncWithShopifyCart();
      
      // Clear mutation flag before final UI update so progress bar can update
      state.isPurchaseOptionMutationInFlight = false;
      state.outstandingAlignmentPromise = null;
      
      // Final UI update with progress bar (now that flag is cleared)
      callbacks.updateCartDisplay();
      
      hideLoadingOverlay(cartId);
    } else {
      state.isPurchaseOptionMutationInFlight = false;
      state.outstandingAlignmentPromise = null;
      hideLoadingOverlay(cartId);
    }
    
  } catch (error) {
    console.error('Error updating cart items for purchase option:', error);
    state.isPurchaseOptionMutationInFlight = false;
    state.outstandingAlignmentPromise = null;
    hideLoadingOverlay(cartId);
  }
  
  if (state.alignmentQueued) {
    state.alignmentQueued = false;
    updateCartItemsForPurchaseOption(cartId, state, options, callbacks);
  }
}

function setupPurchaseOptionHandlers(cartId, state, callbacks) {
    const purchaseOptionSelector = document.querySelector(`.purchase-option-selector-${cartId}`);
    if (!purchaseOptionSelector) return;
    
    const defaultOption = purchaseOptionSelector.getAttribute('data-default-option') || 'subscribe';
    state.currentPurchaseOption = defaultOption;
    
    const subscribeButton = purchaseOptionSelector.querySelector('.subscribe-option');
    const tryOnceButton = purchaseOptionSelector.querySelector('.try-once-option');
    
    // Set initial active class based on default option
    if (defaultOption === 'subscribe') {
      subscribeButton?.classList.add('active');
      tryOnceButton?.classList.remove('active');
    } else {
      tryOnceButton?.classList.add('active');
      subscribeButton?.classList.remove('active');
    }
  
    if (subscribeButton) {
      const discount = subscribeButton.getAttribute('data-discount-percentage');
    if (discount) state.subscribeDiscountPercentage = parseInt(discount);
    }
    if (tryOnceButton) {
      const discount = tryOnceButton.getAttribute('data-discount-percentage');
    if (discount) state.tryOnceDiscountPercentage = parseInt(discount);
    }
    
    if (!window.byobPurchaseOptionState) window.byobPurchaseOptionState = {};
  
    const updateGlobalState = () => {
      window.byobPurchaseOptionState[cartId] = {
        option: state.currentPurchaseOption,
        subscribeDiscount: state.subscribeDiscountPercentage,
        tryOnceDiscount: state.tryOnceDiscountPercentage
      };
    };
    updateGlobalState();
    
    const updatePurchaseOptionLabel = () => {
      const labelElement = document.querySelector(`.purchase-option-current-${cartId}`);
      if (labelElement) {
        if (state.currentPurchaseOption === 'subscribe') {
          labelElement.textContent = `Subscribe & Save ${state.subscribeDiscountPercentage}%`;
        } else if (state.currentPurchaseOption === 'try-once') {
          labelElement.textContent = `${state.tryOnceDiscountPercentage}% Off Sitewide`;
        }
      }
    };

    const updateBenefitsSectionVisibility = () => {
      const benefitsSection = document.querySelector(`.benefits-section-${cartId}`);
      if (benefitsSection) {
        if (state.currentPurchaseOption === 'try-once') {
          benefitsSection.classList.add('hide-for-try-once');
        } else {
          benefitsSection.classList.remove('hide-for-try-once');
        }
      }
    };
  
    const activeButton = purchaseOptionSelector.querySelector('.purchase-option-button-' + cartId + '.active');
    if (activeButton) {
      const activeOption = activeButton.getAttribute('data-option');
      if (activeOption) {
        state.currentPurchaseOption = activeOption;
      }
    }
    
    updatePurchaseOptionLabel();
    updateBenefitsSectionVisibility();
    
    const buttons = purchaseOptionSelector.querySelectorAll('.purchase-option-button-' + cartId);
    buttons.forEach(button => {
      button.addEventListener('click', async function() {
      if (this.classList.contains('active') || state.isPurchaseOptionMutationInFlight) return;
        
        const option = this.getAttribute('data-option');
        if (!option) return;
        
        buttons.forEach(btn => {
          btn.classList.remove('active');
          btn.setAttribute('disabled', 'disabled');
        });
        this.classList.add('active');
      state.currentPurchaseOption = option;
        updateGlobalState();
        updatePurchaseOptionLabel();
        updateBenefitsSectionVisibility();
        
        // Don't update cart display here - it will cause progress bar to move
        // Progress bar will update once at the end after sync completes
        
        document.dispatchEvent(new CustomEvent('byob:purchase-option-changed', {
        detail: { cartId, option, subscribeDiscount: state.subscribeDiscountPercentage, tryOnceDiscount: state.tryOnceDiscountPercentage }
        }));
        
        // Re-enable buttons immediately for better UX
        buttons.forEach(btn => btn.removeAttribute('disabled'));
        
        try {
        await updateCartItemsForPurchaseOption(cartId, state, { syncAfter: true }, callbacks);
        } catch (error) {
          console.error('Error updating cart items:', error);
        }
      });
    });
  }
  
// ============================================================================
// CHECKOUT HANDLERS
// ============================================================================

function handleCheckout() {
  try {
    if (window.upcartRefreshCart) window.upcartRefreshCart();
    if (window.upcartOpenCart) return window.upcartOpenCart();
    const cartBtn = document.getElementById('CartButton');
    if (cartBtn) return cartBtn.click();
    document.dispatchEvent(new CustomEvent('shapes:modalcart:open'));
  } catch (e) {
    console.error('Error opening cart drawer:', e);
  }
}

function setupCheckoutButton(cartId) {
  document.querySelectorAll('.btn-checkout-' + cartId).forEach(button => {
    button.addEventListener('click', (event) => {
      if (button.dataset.hasItems !== 'true') return;
      if (button instanceof HTMLAnchorElement) event.preventDefault();
      handleCheckout();
    });
  });
}

// ============================================================================
// WIDGET SCROLL PREVENTION
// ============================================================================

function setupWidgetScrollPrevention(cartId) {
  const cartElement = document.querySelector('.checkout-cart-sticky-' + cartId);
  if (!cartElement) return;
  
  // Prevent wheel scrolling on the cart widget from affecting the page
  cartElement.addEventListener('wheel', function(e) {
    const cartBody = cartElement.querySelector('.checkout-cart-body-' + cartId);
    
    // If there's a scrollable cart body, allow scrolling within it
    if (cartBody && cartBody.scrollHeight > cartBody.clientHeight) {
      const isAtTop = cartBody.scrollTop <= 0;
      const isAtBottom = cartBody.scrollTop + cartBody.clientHeight >= cartBody.scrollHeight - 1;
      
      // Only prevent page scroll when at boundaries and trying to scroll beyond
      if ((isAtTop && e.deltaY < 0) || (isAtBottom && e.deltaY > 0)) {
        e.preventDefault();
        e.stopPropagation();
      }
    } else {
      // If cart body is not scrollable or doesn't exist, prevent all wheel events within widget
      e.preventDefault();
      e.stopPropagation();
    }
  }, { passive: false });
  
  // Prevent touch scrolling on the cart widget from affecting the page
  let touchStartY = 0;
  let touchStartElement = null;
  
  cartElement.addEventListener('touchstart', function(e) {
    touchStartY = e.touches[0].clientY;
    touchStartElement = e.target;
  }, { passive: true });
  
  cartElement.addEventListener('touchmove', function(e) {
    const cartBody = cartElement.querySelector('.checkout-cart-body-' + cartId);
    
    // If touch started in scrollable cart body, allow scrolling within it
    if (cartBody && cartBody.contains(touchStartElement) && cartBody.scrollHeight > cartBody.clientHeight) {
      const touchCurrentY = e.touches[0].clientY;
      const deltaY = touchCurrentY - touchStartY;
      const isAtTop = cartBody.scrollTop <= 0;
      const isAtBottom = cartBody.scrollTop + cartBody.clientHeight >= cartBody.scrollHeight - 1;
      
      // Only prevent page scroll when at boundaries and trying to scroll beyond
      if ((isAtTop && deltaY > 0) || (isAtBottom && deltaY < 0)) {
        e.preventDefault();
        e.stopPropagation();
      }
    } else if (cartElement.contains(touchStartElement)) {
      // If touch started in cart widget (but not in scrollable body), prevent page scroll
      e.preventDefault();
      e.stopPropagation();
    }
  }, { passive: false });
}

// ============================================================================
// TOGGLE CART
// ============================================================================

function toggleCart(cartId) {
  const cartElement = document.querySelector('.checkout-cart-sticky-' + cartId);
  if (!cartElement) return;
  
  const toggleUpdater = window['updateToggleButtons_' + cartId];
  const isCollapsed = cartElement.classList.contains('cart-collapsed');
  
  cartElement.classList.toggle('cart-collapsed');
  cartElement.classList.remove('scroll-adjusted');
  
  if (typeof toggleUpdater === 'function') {
    toggleUpdater(!isCollapsed);
  } else {
    const arrowUp = window.checkoutCartToggleArrowUp || DEFAULT_SVGS.toggleArrowUp;
    const arrowDown = window.checkoutCartToggleArrowDown || DEFAULT_SVGS.toggleArrowDown;
    const arrow = isCollapsed ? arrowDown : arrowUp;
    document.querySelectorAll(`.checkout-cart-sticky-${cartId} .cart-toggle-${cartId}`).forEach(btn => btn.innerHTML = arrow);
    document.querySelectorAll(`.checkout-cart-fixed-${cartId} .cart-toggle-${cartId}`).forEach(btn => btn.innerHTML = arrowUp);
  }
  
  if (!isCollapsed && isMobile()) {
    const updateFn = window['updateCartDisplay_' + cartId];
    if (updateFn) setTimeout(() => updateFn(), CART_CONSTANTS.SYNC_DELAY);
  }
}

// ============================================================================
// MAIN INITIALIZATION FUNCTION
// ============================================================================

window.initCheckoutCartSnippet = function(cartId) {
  const state = createCartState(cartId);
  const elements = getCartElements(cartId);
  
  const callbacks = {
    syncWithShopifyCart: () => syncWithShopifyCart(cartId, state, elements, callbacks),
    updateCartDisplay: () => updateCartDisplay(cartId, state, elements, callbacks),
    addItemToCart: (itemData) => addItemToCart(cartId, itemData, state, elements, callbacks),
    removeItemFromCart: (variantId) => removeItemFromCart(cartId, variantId, state, elements, callbacks),
    updateCartItemQuantity: (variantId, newQuantity, itemData) => updateCartItemQuantity(cartId, variantId, newQuantity, itemData, callbacks),
    updateCartItemSellingPlan: (variantId, sellingPlanId, options) => updateCartItemSellingPlan(cartId, variantId, sellingPlanId, state, options),
    updateCartItemsForPurchaseOption: (options) => updateCartItemsForPurchaseOption(cartId, state, options, callbacks),
    setupCartQuantitySelector: (itemElement, item) => setupCartQuantitySelector(cartId, itemElement, item, state, callbacks),
    setupDeliveryFrequencySelector: (itemElement, item) => setupDeliveryFrequencySelector(cartId, itemElement, item, state, callbacks),
    toggleCart: () => toggleCart(cartId)
  };
  
  window['addItemToCartSnippet_' + cartId] = callbacks.addItemToCart;
  window['removeItemFromCartSnippet_' + cartId] = callbacks.removeItemFromCart;
  window['updateCartItemQuantity_' + cartId] = callbacks.updateCartItemQuantity;
  window['updateDiscountBadgeStates_' + cartId] = (cartTotal) => updateDiscountBadgeStates(cartId, cartTotal, state.currentCartTotal);
  window['syncWithShopifyCart_' + cartId] = callbacks.syncWithShopifyCart;
  window['updateToggleButtons_' + cartId] = (isCollapsed) => updateToggleButtons(cartId, isCollapsed);
  window['updateCartDisplay_' + cartId] = callbacks.updateCartDisplay;
  
  window.checkoutCartInstances.push({
    cartId: cartId,
    syncFunction: callbacks.syncWithShopifyCart
  });
  
  if (!window.addItemToCartSnippet) {
    window.addItemToCartSnippet = function(itemData) {
      if (!itemData.productHandle) {
        itemData.productHandle = inferProductHandle(itemData);
      }
      const foundCartId = findCartId();
      if (foundCartId && window['addItemToCartSnippet_' + foundCartId]) {
        return window['addItemToCartSnippet_' + foundCartId](itemData);
      }
    };
  }
  
  if (!window.removeItemFromCartSnippet) {
    window.removeItemFromCartSnippet = function(variantId) {
      const foundCartId = findCartId();
      if (foundCartId && window['removeItemFromCartSnippet_' + foundCartId]) {
        window['removeItemFromCartSnippet_' + foundCartId](variantId);
      }
    };
  }
  
  if (!window.syncCheckoutCartWithShopify) {
    window.syncCheckoutCartWithShopify = () => {
      window.checkoutCartInstances?.forEach(instance => instance.syncFunction());
    };
  }
  
  setupCheckoutButton(cartId);
  
  const touchHandlers = createTouchHandlers(cartId, state, callbacks);
  
  window.addEventListener('scroll', () => handleMobileScrollAdjustment(cartId, state), { passive: true });
  window.addEventListener('scroll', () => updateFixedCartVisibility(cartId, state, elements), { passive: true });
  window.addEventListener('resize', () => updateFixedCartVisibility(cartId, state, elements));
  window.addEventListener('resize', () => {
    setTimeout(() => callbacks.updateCartDisplay(), CART_CONSTANTS.SYNC_DELAY);
  });
  
  updateFixedCartVisibility(cartId, state, elements);
  
  // Setup scroll prevention for widget area
  setupWidgetScrollPrevention(cartId);
  
  setTimeout(() => {
    state.isInitialized = true;
    
    if (isMobile() && elements.stickyCartElement) {
      elements.stickyCartElement.classList.add('cart-collapsed');
      updateToggleButtons(cartId, true);
    }
    
    callbacks.syncWithShopifyCart();
    
    setTimeout(() => {
      updateToggleButtons(cartId, !!elements.stickyCartElement?.classList.contains('cart-collapsed'));
      setupTouchDrag(cartId, touchHandlers);
      setupPurchaseOptionHandlers(cartId, state, callbacks);
      setTimeout(() => updateDiscountBadgeStates(cartId, undefined, state.currentCartTotal), 300);
    }, 200);
  }, CART_CONSTANTS.INITIAL_SYNC_DELAY);
};

// ============================================================================
// AUTO-INITIALIZATION
// ============================================================================

function initializeCartSnippets() {
  document.querySelectorAll('[class*="checkout-cart-sticky-"]').forEach(cartElement => {
    const match = cartElement.className.match(/checkout-cart-sticky-([a-zA-Z0-9_-]+)/);
    if (match) {
      const cartId = match[1];
      if (!window['checkoutCartSnippetInitialized_' + cartId]) {
        initCheckoutCartSnippet(cartId);
        window['checkoutCartSnippetInitialized_' + cartId] = true;
      }
    }
  });
}

// ============================================================================
// INITIALIZE GLOBALS
// ============================================================================

initializeGlobalState();
setupUpCartIntegration();
document.addEventListener('DOMContentLoaded', initializeCartSnippets);
setTimeout(initializeCartSnippets, 2000);
